import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import en from './locales/en.json';

export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇺🇸' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'zh-CN', label: '简体中文', flag: '🇨🇳' },
  { code: 'zh-HK', label: '繁體中文', flag: '🇭🇰' },
  { code: 'ja', label: '日本語', flag: '🇯🇵' },
  { code: 'pt-BR', label: 'Português', flag: '🇧🇷' },
  { code: 'ru', label: 'Русский', flag: '🇷🇺' },
] as const;

export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number]['code'];

// Lazy loaders: only fetch the locale JSON when the user actually selects it.
// Keeps the initial bundle ~250KB lighter on mobile.
const localeLoaders: Record<Exclude<SupportedLanguage, 'en'>, () => Promise<any>> = {
  es: () => import('./locales/es.json'),
  fr: () => import('./locales/fr.json'),
  de: () => import('./locales/de.json'),
  it: () => import('./locales/it.json'),
  'zh-CN': () => import('./locales/zh-CN.json'),
  'zh-HK': () => import('./locales/zh-HK.json'),
  ja: () => import('./locales/ja.json'),
  'pt-BR': () => import('./locales/pt-BR.json'),
  ru: () => import('./locales/ru.json'),
};

const loadedLanguages = new Set<string>(['en']);

async function ensureLanguageLoaded(lng: string): Promise<void> {
  if (loadedLanguages.has(lng)) return;
  const loader = (localeLoaders as Record<string, () => Promise<any>>)[lng];
  if (!loader) return;
  try {
    const mod = await loader();
    i18n.addResourceBundle(lng, 'translation', mod.default ?? mod, true, true);
    loadedLanguages.add(lng);
  } catch (err) {
    console.warn(`[i18n] Failed to load locale "${lng}":`, err);
  }
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
    },
    fallbackLng: 'en',
    supportedLngs: ['en', 'es', 'fr', 'de', 'it', 'zh-CN', 'zh-HK', 'ja', 'pt-BR', 'ru'],
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      lookupLocalStorage: 'amorette_language',
      caches: ['localStorage'],
    },
    saveMissing: true,
    missingKeyHandler: (lngs, _ns, key) => {
      console.warn(`[i18n] Missing translation key: "${key}" for languages: ${lngs.join(', ')}`);
    },
  });

// Load the detected language on boot (if not English) and on every subsequent change.
i18n.on('languageChanged', (lng) => {
  void ensureLanguageLoaded(lng);
});

if (i18n.language && i18n.language !== 'en') {
  void ensureLanguageLoaded(i18n.language);
}

export { ensureLanguageLoaded };
export default i18n;
